import { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup } from 'react-leaflet';
import { toast } from 'sonner';
import { Route } from 'lucide-react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useVehicleRegistry } from './useVehicleRegistry';
import { formatUkTime, formatDwell } from './historicalDataFormat';
import 'leaflet/dist/leaflet.css';

const todayStr = () => new Date().toISOString().slice(0, 10);

/** One vehicle's full-day breadcrumb line + auto-detected dwell "stops"
 * (dots), independent of any Transport run/planned-stop data — works for
 * E70 even though its days aren't auto-populated with planned stops, and
 * for any vehicle in the full Samsara fleet, not just branch-mapped ones. */
export const VehicleBreadcrumbViewer = () => {
  const { vehicles, loading: loadingVehicles } = useVehicleRegistry();
  const [samsaraId, setSamsaraId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const selectedVehicle = useMemo(() => vehicles.find((v) => v.samsara_vehicle_id === samsaraId), [vehicles, samsaraId]);

  const load = async () => {
    if (!selectedVehicle) { toast.error('Select a vehicle'); return; }
    setLoading(true);
    setData(null);
    try {
      const vehiclePathSegment = selectedVehicle.registry_id || selectedVehicle.samsara_vehicle_id;
      const res = await api.get(`${API}/transport/samsara/breadcrumb-with-stops/${vehiclePathSegment}/${date}`, {
        params: { samsara_vehicle_id: selectedVehicle.samsara_vehicle_id },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load breadcrumb history');
    } finally {
      setLoading(false);
    }
  };

  const path = (data?.points || []).map((p) => [p.lat, p.lng]);
  const center = path[0] || (data?.stops?.[0] ? [data.stops[0].lat, data.stops[0].lng] : [52.48, -1.9]);

  return (
    <div className="space-y-4" data-testid="vehicle-breadcrumb-viewer">
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="space-y-1.5">
            <Label>Vehicle</Label>
            <Select value={samsaraId} onValueChange={setSamsaraId} disabled={loadingVehicles}>
              <SelectTrigger className="bg-white" data-testid="hdc-breadcrumb-vehicle-select">
                <SelectValue placeholder={loadingVehicles ? 'Loading…' : 'Select vehicle'} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {vehicles.map((v) => (
                  <SelectItem key={v.samsara_vehicle_id} value={v.samsara_vehicle_id}>
                    {v.vehicle_registration || v.name}{v.branch_id ? ` (${v.branch_id})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="hdc-breadcrumb-date">Date</Label>
            <Input id="hdc-breadcrumb-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white" data-testid="hdc-breadcrumb-date" />
          </div>
          <Button onClick={load} disabled={loading} className="text-white gap-2" style={{ backgroundColor: '#ee7203' }} data-testid="hdc-breadcrumb-load-btn">
            <Route className="w-4 h-4" /> {loading ? 'Loading…' : 'Load'}
          </Button>
        </div>
      </div>

      {data && (
        path.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8" data-testid="hdc-breadcrumb-empty">No Samsara route history found for this vehicle/day</div>
        ) : (
          <>
            <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height: 420 }}>
              <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_2uap_1_93da0402da1fce587cd7c547" attribution='&copy; OpenStreetMap contributors &copy; CARTO' />
                <Polyline positions={path} pathOptions={{ color: '#38bdf8', weight: 3, opacity: 0.85 }} />
                {(data.stops || []).map((s, i) => (
                  <CircleMarker key={i} center={[s.lat, s.lng]} radius={9} pathOptions={{ color: '#22c55e', weight: 2, fillColor: '#22c55e', fillOpacity: 0.85 }} data-testid={`hdc-stop-dot-${i}`}>
                    <Popup>
                      <div className="text-xs space-y-0.5">
                        <div className="font-bold">{s.nearest_postcode || 'Stop'}</div>
                        <div>{formatUkTime(s.arrived_at)} → {formatUkTime(s.left_at)}</div>
                        <div className="font-semibold text-emerald-700">Dwell: {formatDwell(s.dwell_minutes)}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Detected Stops ({(data.stops || []).length})</div>
              {(data.stops || []).length === 0 ? (
                <div className="text-xs text-gray-400">No dwell stops detected this day</div>
              ) : (
                <div className="space-y-1">
                  {data.stops.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-1.5" data-testid={`hdc-stop-row-${i}`}>
                      <span className="text-gray-700 font-semibold">{s.nearest_postcode || `${s.lat.toFixed(4)}, ${s.lng.toFixed(4)}`}</span>
                      <span className="text-gray-500">{formatUkTime(s.arrived_at)} → {formatUkTime(s.left_at)}</span>
                      <span className="font-bold text-emerald-600">{formatDwell(s.dwell_minutes)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )
      )}
    </div>
  );
};

export default VehicleBreadcrumbViewer;
